import React, { useState, useEffect } from 'react';
import { getAIConfigs, saveAIConfig, deleteAIConfig, updateModelConfig, getSquashConfig, saveSquashConfig, deleteSquashConfig, testSquashConnection, getOllamaModels, testOllamaConnection, safeErrorMessage } from '../services/api';
import { useToast } from '../App';
import { HiOutlineCog, HiOutlineKey, HiOutlineTrash, HiOutlineCheck, HiOutlineSave, HiOutlineRefresh } from 'react-icons/hi';
import { FiCpu, FiShield, FiSliders, FiThermometer } from 'react-icons/fi';
import { MODELS_BY_PROVIDER, PROVIDERS } from '../constants/aiModels';

export default function Configuration() {
  const addToast = useToast();
  const [configs, setConfigs] = useState([]);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [modelConfig, setModelConfig] = useState({
    openai: { model: 'gpt-5.6-sol', temperature: 0.2, max_tokens: null },
    claude: { model: 'claude-opus-4.5', temperature: 0.2, max_tokens: null },
    gemini: { model: 'gemini-3-pro-preview', temperature: 0.2, max_tokens: null },
    ollama: { model: '', temperature: 0.2, max_tokens: null },
  });
  const [savingModel, setSavingModel] = useState(false);

  // Ollama state
  const [ollamaUrl, setOllamaUrl] = useState('');
  const [ollamaSaving, setOllamaSaving] = useState(false);
  const [ollamaTesting, setOllamaTesting] = useState(false);
  const [ollamaTestResult, setOllamaTestResult] = useState(null);
  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelsLoading, setOllamaModelsLoading] = useState(false);

  // Squash TM config state
  const [squashUrl, setSquashUrl] = useState('');
  const [squashToken, setSquashToken] = useState('');
  const [squashConfig, setSquashConfig] = useState(null);
  const [squashSaving, setSquashSaving] = useState(false);
  const [squashTesting, setSquashTesting] = useState(false);
  const [squashTestResult, setSquashTestResult] = useState(null);

  const loadConfigs = async () => {
    try {
      const { data } = await getAIConfigs();
      setConfigs(data);
      // Sync model config from server (functional update avoids stale closure)
      setModelConfig((prev) => {
        const updated = { ...prev };
        data.forEach((c) => {
          if (updated[c.provider] !== undefined) {
            updated[c.provider] = {
              model: (MODELS_BY_PROVIDER[c.provider] || []).some((m) => m.value === c.model)
                ? c.model
                : (c.provider === 'ollama' ? c.model : '') || MODELS_BY_PROVIDER[c.provider]?.[0]?.value || '',
              temperature: c.temperature !== undefined ? c.temperature : 0.2,
              max_tokens: c.max_tokens ?? null,
            };
          }
        });
        return updated;
      });
      // Récupérer l'URL Ollama masquée (stockée dans api_key_masked)
      const ollamaCfg = data.find((c) => c.provider === 'ollama');
      if (ollamaCfg) {
        // api_key_masked contient l'URL masquée — on l'affiche en placeholder
        setOllamaUrl('');
      }
    } catch (err) {
      addToast('error', 'Impossible de charger les configurations IA.');
    }
  };

  const loadOllamaModels = async () => {
    setOllamaModelsLoading(true);
    try {
      const { data } = await getOllamaModels();
      setOllamaModels(data.models || []);
    } catch {
      setOllamaModels([]);
    }
    setOllamaModelsLoading(false);
  };

  const loadSquashConfig = async () => {
    try {
      const { data } = await getSquashConfig();
      setSquashConfig(data);
      setSquashUrl(data.url || '');
    } catch {
      setSquashConfig(null);
    }
  };

  useEffect(() => { loadConfigs(); loadSquashConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les modèles Ollama quand on sélectionne ce provider
  useEffect(() => {
    if (selectedProvider === 'ollama' && getConfig('ollama')) {
      loadOllamaModels();
    }
  }, [selectedProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const getConfig = (provider) => configs.find((c) => c.provider === provider);

  const handleSave = async (provider) => {
    const key = inputs[provider];
    if (!key || !key.trim()) {
      addToast('warning', 'Veuillez entrer une clé API.');
      return;
    }
    setSaving((s) => ({ ...s, [provider]: true }));
    try {
      await saveAIConfig({
        provider,
        api_key: key.trim(),
        model: modelConfig[provider]?.model || '',
        temperature: modelConfig[provider]?.temperature ?? 0.2,
        max_tokens: modelConfig[provider]?.max_tokens ?? null,
      });
      setInputs((prev) => ({ ...prev, [provider]: '' }));
      await loadConfigs();
      addToast('success', `Clé API ${provider} sauvegardée et chiffrée.`);
    } catch (err) {
      const msg = err.response?.status === 403
        ? 'Accès réservé aux administrateurs.'
        : safeErrorMessage(err, 'Erreur lors de la sauvegarde.');
      addToast('error', msg);
    }
    setSaving((s) => ({ ...s, [provider]: false }));
  };

  const handleDelete = async (provider) => {
    try {
      await deleteAIConfig(provider);
      await loadConfigs();
      addToast('success', `Clé API ${provider} supprimée.`);
    } catch { }
  };

  const handleSaveModelConfig = async () => {
    const config = getConfig(selectedProvider);
    if (!config) {
      addToast('warning', 'Veuillez d\'abord configurer une clé API pour ce provider.');
      return;
    }
    setSavingModel(true);
    try {
      await updateModelConfig(selectedProvider, modelConfig[selectedProvider]);
      addToast('success', `Configuration du modèle ${selectedProvider} sauvegardée.`);
    } catch (err) {
      const msg = err.response?.status === 403
        ? 'Accès réservé aux administrateurs.'
        : safeErrorMessage(err, 'Erreur lors de la sauvegarde.');
      addToast('error', msg);
    }
    setSavingModel(false);
  };

  const currentModels = MODELS_BY_PROVIDER[selectedProvider] || [];
  const currentModelConfig = modelConfig[selectedProvider] || { model: '', temperature: 0.2, max_tokens: null };
  const providerInfo = PROVIDERS.find((p) => p.value === selectedProvider);
  const isProviderConfigured = !!getConfig(selectedProvider);
  // Plafond de sortie du modèle sélectionné (undefined pour Ollama → pas de plafond connu)
  const selectedModelMeta = currentModels.find((m) => m.value === currentModelConfig.model);
  // Les modeles de raisonnement rejettent toute temperature != 1 (erreur 400 cote API)
  const ignoresTemperature = !!selectedModelMeta?.noTemperature;
  const modelCeiling = selectedModelMeta?.maxTokens;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><HiOutlineCog className="me-2" />Configuration</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Paramètres de l'application et clés API
          </p>
        </div>
      </div>
      {/* ===== Section 1 : Squash TM ===== */}
      <div className="row g-4 mb-4">
        <div className="col-md-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <HiOutlineRefresh />
              <span>Squash TM - Synchronisation</span>
              {squashConfig ? (
                <span className="meta-tag ms-auto" style={{ background: 'var(--green-100)', color: 'var(--green-700)' }}>
                  <HiOutlineCheck /> Configuré
                </span>
              ) : (
                <span className="meta-tag ms-auto" style={{ background: 'var(--gray-100)', color: 'var(--gray-500)' }}>
                  Non configuré
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-3 p-2" style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                <FiShield style={{ color: 'var(--green-600)', flexShrink: 0 }} />
                Le token est chiffré (AES-256) avant d'être stocké. Créez un Personal API Token dans Squash TM (Mon compte).
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="field-squash-url" style={{ fontSize: '0.82rem', fontWeight: 600 }}>URL Squash TM</label>
                <input
                  id="field-squash-url"
                  type="text"
                  className="form-control"
                  placeholder="http://localhost:8080/squash"
                  value={squashUrl}
                  onChange={(e) => setSquashUrl(e.target.value)}
                  style={{ fontSize: '0.82rem' }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="field-squash-token" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Bearer Token</label>
                <input
                  id="field-squash-token"
                  type="password"
                  className="form-control"
                  placeholder={squashConfig ? 'Nouveau token pour remplacer...' : 'Collez votre Personal API Token'}
                  value={squashToken}
                  onChange={(e) => setSquashToken(e.target.value)}
                  style={{ fontSize: '0.82rem' }}
                  autoComplete="off"
                />
                {squashConfig && (
                  <div className="form-text" style={{ fontSize: '0.72rem' }}>
                    Token actuel : <code>{squashConfig.token_masked}</code>
                  </div>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={squashSaving}
                  onClick={async () => {
                    if (!squashUrl.trim() || !squashToken.trim()) {
                      addToast('warning', 'Veuillez renseigner l\'URL et le token.');
                      return;
                    }
                    setSquashSaving(true);
                    try {
                      await saveSquashConfig({ url: squashUrl.trim(), token: squashToken.trim() });
                      setSquashToken('');
                      await loadSquashConfig();
                      addToast('success', 'Configuration Squash TM sauvegardée.');
                    } catch (err) {
                      const msg = err.response?.status === 403
                        ? 'Accès réservé aux administrateurs.'
                        : safeErrorMessage(err, 'Erreur lors de la sauvegarde.');
                      addToast('error', msg);
                    }
                    setSquashSaving(false);
                  }}
                >
                  <HiOutlineSave className="me-1" />
                  {squashSaving ? '...' : 'Sauvegarder'}
                </button>

                <button
                  className="btn btn-outline-info btn-sm"
                  disabled={squashTesting || !squashConfig}
                  onClick={async () => {
                    setSquashTesting(true);
                    setSquashTestResult(null);
                    try {
                      const { data } = await testSquashConnection();
                      setSquashTestResult(data);
                      addToast(data.ok ? 'success' : 'error', data.message);
                    } catch (err) {
                      addToast('error', safeErrorMessage(err, 'Erreur lors du test.'));
                    }
                    setSquashTesting(false);
                  }}
                >
                  {squashTesting ? '...' : 'Tester la connexion'}
                </button>

                {squashConfig && (
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={async () => {
                      try {
                        await deleteSquashConfig();
                        setSquashConfig(null);
                        setSquashUrl('');
                        setSquashToken('');
                        setSquashTestResult(null);
                        addToast('success', 'Configuration Squash TM supprimée.');
                      } catch (err) {
                        addToast('error', safeErrorMessage(err, 'Erreur lors de la suppression.'));
                      }
                    }}
                    title="Supprimer la configuration"
                  >
                    <HiOutlineTrash />
                  </button>
                )}
              </div>

              {squashTestResult && (
                <div className={`mt-3 p-2 rounded ${squashTestResult.ok ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}
                  style={{ fontSize: '0.78rem', color: squashTestResult.ok ? 'var(--green-700)' : 'var(--danger)' }}>
                  {squashTestResult.ok ? <HiOutlineCheck className="me-1" /> : null}
                  {squashTestResult.message}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Provider status — à côté de Squash TM */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <FiCpu />
              <span>Statut des providers</span>
            </div>
            <div className="card-body">
              {PROVIDERS.map((p) => {
                const config = getConfig(p.value);
                return (
                  <button type="button"
                    key={p.value}
                    className="d-flex align-items-center justify-content-between mb-2"
                    style={{
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-sm)',
                      background: selectedProvider === p.value ? `${p.color}10` : 'transparent',
                      transition: 'background 0.15s ease',
                      border: 'none',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      width: '100%',
                    }}
                    onClick={() => setSelectedProvider(p.value)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <img src={p.logo} alt={p.label} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                      <span style={{ fontSize: '0.82rem' }}>{p.label}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {config?.model && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', fontFamily: 'Consolas, monospace' }}>
                          {config.model}
                        </span>
                      )}
                      {config ? (
                        <span className="status-badge status-OK">Actif</span>
                      ) : (
                        <span className="status-badge status-NT">Non configuré</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Section 2 : Clés API IA ===== */}
      <div className="row g-4">
        {/* AI API Keys */}
        <div className="col-md-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <HiOutlineKey />
              <span>Clés API - Intelligence Artificielle</span>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-4 p-2" style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                <FiShield style={{ color: 'var(--green-600)', flexShrink: 0 }} />
                Les clés sont chiffrées (AES-256) avant d'être stockées en base de données.
              </div>

              {PROVIDERS.map((p) => {
                const config = getConfig(p.value);
                const isSelected = selectedProvider === p.value;
                const isOllama = p.value === 'ollama';
                return (
                  <button
                    type="button"
                    key={p.value}
                    className="mb-3 p-3"
                    style={{
                      borderRadius: 'var(--radius-md)',
                      border: isSelected ? `2px solid ${p.color}` : '1px solid var(--gray-100)',
                      background: isSelected ? `${p.color}08` : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      width: '100%',
                      display: 'block',
                    }}
                    onClick={() => setSelectedProvider(p.value)}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <img src={p.logo} alt={p.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.label}</span>
                        {isOllama && (
                          <span style={{ fontSize: '0.65rem', background: '#e0e7ff', color: '#3730a3', padding: '1px 8px', borderRadius: '10px', fontWeight: 500 }}>
                            LLM Local
                          </span>
                        )}
                        {isSelected && (
                          <span style={{
                            fontSize: '0.65rem',
                            background: p.color,
                            color: '#fff',
                            padding: '1px 8px',
                            borderRadius: '10px',
                            fontWeight: 500,
                          }}>
                            Sélectionné
                          </span>
                        )}
                      </div>
                      {config && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="meta-tag" style={{ background: 'var(--green-100)', color: 'var(--green-700)' }}>
                            <HiOutlineCheck /> {isOllama ? 'Connecté' : 'Configuré'}
                          </span>
                          {!isOllama && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontFamily: 'Consolas, monospace' }}>
                              {config.api_key_masked}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {isOllama ? (
                      /* ===== Section spéciale Ollama : URL au lieu de clé API ===== */
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-2 p-2" style={{ background: '#e0e7ff40', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', color: '#3730a3' }}>
                          <FiCpu style={{ flexShrink: 0 }} />
                          Aucune clé API requise — Ollama tourne en local sur votre machine.
                        </div>
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={config ? config.api_key_masked + ' (nouvelle URL pour remplacer)' : 'http://localhost:11434'}
                            value={ollamaUrl}
                            onChange={(e) => setOllamaUrl(e.target.value)}
                            style={{ fontSize: '0.82rem' }}
                          />
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={ollamaSaving}
                            style={{ whiteSpace: 'nowrap', background: p.color, borderColor: p.color }}
                            onClick={async () => {
                              const url = ollamaUrl.trim() || 'http://localhost:11434';
                              setOllamaSaving(true);
                              try {
                                await saveAIConfig({ provider: 'ollama', api_key: url, model: modelConfig.ollama?.model || '', temperature: modelConfig.ollama?.temperature ?? 0.2, max_tokens: modelConfig.ollama?.max_tokens ?? null });
                                setOllamaUrl('');
                                await loadConfigs();
                                addToast('success', 'URL Ollama sauvegardée.');
                              } catch (err) {
                                const msg = err.response?.status === 403
                                  ? 'Accès réservé aux administrateurs.'
                                  : safeErrorMessage(err, 'Erreur lors de la sauvegarde.');
                                addToast('error', msg);
                              }
                              setOllamaSaving(false);
                            }}
                          >
                            {ollamaSaving ? '...' : config ? 'Mettre à jour' : 'Sauvegarder'}
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            disabled={ollamaTesting || !config}
                            onClick={async () => {
                              setOllamaTesting(true);
                              setOllamaTestResult(null);
                              try {
                                const { data: res } = await testOllamaConnection();
                                setOllamaTestResult(res);
                                if (res.ok) {
                                  setOllamaModels(res.models || []);
                                  addToast('success', res.message);
                                } else {
                                  addToast('error', res.message);
                                }
                              } catch (err) {
                                addToast('error', safeErrorMessage(err, 'Erreur de connexion Ollama.'));
                              }
                              setOllamaTesting(false);
                            }}
                          >
                            {ollamaTesting ? '...' : 'Tester'}
                          </button>
                          {config && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={async () => {
                                try {
                                  await deleteAIConfig('ollama');
                                  await loadConfigs();
                                  setOllamaTestResult(null);
                                  setOllamaModels([]);
                                  addToast('success', 'Configuration Ollama supprimée.');
                                } catch { }
                              }}
                              title="Supprimer"
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                        {ollamaTestResult && (
                          <div className={`p-2 rounded`} style={{ fontSize: '0.75rem', background: ollamaTestResult.ok ? 'var(--green-100)' : '#fee2e2', color: ollamaTestResult.ok ? 'var(--green-700)' : 'var(--danger)' }}>
                            {ollamaTestResult.ok && <HiOutlineCheck className="me-1" />}
                            {ollamaTestResult.message}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* ===== Providers cloud : champ clé API standard ===== */
                      <div className="d-flex gap-2">
                        <input
                          type="password"
                          className="form-control"
                          placeholder={config ? 'Nouvelle clé pour remplacer...' : p.placeholder}
                          value={inputs[p.value] || ''}
                          onChange={(e) => setInputs({ ...inputs, [p.value]: e.target.value })}
                          style={{ fontSize: '0.82rem' }}
                          autoComplete="off"
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSave(p.value)}
                          disabled={saving[p.value]}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {saving[p.value] ? '...' : config ? 'Mettre à jour' : 'Sauvegarder'}
                        </button>
                        {config && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(p.value)}
                            title="Supprimer"
                          >
                            <HiOutlineTrash />
                          </button>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Config Model - Dynamic */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2" style={{ borderBottom: `2px solid ${providerInfo?.color || 'var(--gray-200)'}` }}>
              <FiSliders style={{ color: providerInfo?.color }} />
              <span>Config Model</span>
              <img src={providerInfo?.logo} alt="" style={{ width: 18, height: 18, objectFit: 'contain', marginLeft: 'auto' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{providerInfo?.label}</span>
            </div>
            <div className="card-body">
              {!isProviderConfigured ? (
                <div className="text-center py-4">
                  <FiCpu style={{ fontSize: '2rem', color: 'var(--gray-300)', marginBottom: '0.5rem' }} />
                  <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>
                    {selectedProvider === 'ollama'
                      ? <>Configurez d'abord l'URL de votre serveur <strong>Ollama</strong> à gauche.</>
                      : <>Configurez d'abord une clé API pour <strong>{providerInfo?.label}</strong> pour accéder aux paramètres du modèle.</>
                    }
                  </p>
                </div>
              ) : selectedProvider === 'ollama' ? (
                /* ===== Config Model Ollama : modèles dynamiques ===== */
                <>
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="form-label d-flex align-items-center gap-2 mb-0" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                        <FiCpu style={{ color: providerInfo?.color }} />
                        Modèles installés
                      </span>
                      <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.72rem' }} onClick={loadOllamaModels} disabled={ollamaModelsLoading}>
                        {ollamaModelsLoading ? '...' : 'Rafraîchir'}
                      </button>
                    </div>
                    {ollamaModels.length === 0 ? (
                      <div className="text-center py-3" style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                        {ollamaModelsLoading ? 'Chargement...' : (
                          <>
                            Aucun modèle trouvé. Vérifiez qu'Ollama est démarré et testez la connexion.<br />
                            <code style={{ fontSize: '0.72rem' }}>ollama pull llama3.2</code>
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {ollamaModels.map((m) => {
                          const isSelected = currentModelConfig.model === m;
                          return (
                            <button type="button"
                              key={m}
                              onClick={() => setModelConfig({ ...modelConfig, ollama: { ...currentModelConfig, model: m } })}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: isSelected ? `2px solid ${providerInfo?.color}` : '1px solid var(--gray-200)',
                                background: isSelected ? `${providerInfo?.color}10` : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                font: 'inherit',
                                color: 'inherit',
                                textAlign: 'left',
                                width: '100%',
                              }}
                            >
                              <FiCpu style={{ color: isSelected ? providerInfo?.color : 'var(--gray-400)', flexShrink: 0 }} />
                              <span style={{ fontWeight: isSelected ? 600 : 400, fontSize: '0.82rem', fontFamily: 'Consolas, monospace' }}>{m}</span>
                              {isSelected && <HiOutlineCheck className="ms-auto" style={{ color: providerInfo?.color }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Temperature slider */}
                  <div className="mb-4">
                    <label className="form-label d-flex align-items-center justify-content-between" htmlFor="field-ollama-temperature" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <span className="d-flex align-items-center gap-2">
                        <FiThermometer style={{ color: providerInfo?.color }} />
                        Température
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: providerInfo?.color, fontFamily: 'Consolas, monospace', background: `${providerInfo?.color}15`, padding: '2px 10px', borderRadius: '6px' }}>
                        {currentModelConfig.temperature.toFixed(1)}
                      </span>
                    </label>
                    <input
                      id="field-ollama-temperature"
                      type="range"
                      className="form-range"
                      min="0" max="2" step="0.1"
                      value={currentModelConfig.temperature}
                      onChange={(e) => setModelConfig({ ...modelConfig, ollama: { ...currentModelConfig, temperature: Number.parseFloat(e.target.value) } })}
                      style={{ accentColor: providerInfo?.color }}
                    />
                    <div className="d-flex justify-content-between" style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>
                      <span>Précis (0.0)</span><span>Créatif (1.0)</span>
                    </div>
                  </div>

                  {/* Max tokens (sortie) — Ollama (num_predict) */}
                  <div className="mb-4">
                    <label className="form-label d-flex align-items-center gap-2" htmlFor="field-ollama-maxtokens" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <FiSliders style={{ color: providerInfo?.color }} />
                      Max tokens (sortie)
                    </label>
                    <input
                      id="field-ollama-maxtokens"
                      type="number"
                      min="256"
                      step="1024"
                      className="form-control"
                      placeholder="Illimité (num_predict par défaut)"
                      value={currentModelConfig.max_tokens ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        setModelConfig({
                          ...modelConfig,
                          ollama: { ...currentModelConfig, max_tokens: v === '' ? null : Number.parseInt(v, 10) },
                        });
                      }}
                      style={{ fontSize: '0.82rem' }}
                    />
                    <div className="form-text" style={{ fontSize: '0.72rem' }}>
                      Limite de tokens générés (option <code>num_predict</code>). Laissez vide pour la valeur par défaut du modèle.
                    </div>
                  </div>

                  <button
                    className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleSaveModelConfig}
                    disabled={savingModel || !currentModelConfig.model}
                    style={{ background: providerInfo?.color, borderColor: providerInfo?.color }}
                  >
                    <HiOutlineSave />
                    {savingModel ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
                  </button>
                </>
              ) : (
                <>
                  {/* Model selector (cloud providers) */}
                  <div className="mb-4">
                    <span className="form-label d-flex align-items-center gap-2" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <FiCpu style={{ color: providerInfo?.color }} />
                      Modèle
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {currentModels.map((m) => {
                        const isSelected = currentModelConfig.model === m.value;
                        const tierColors = {
                          'Premium': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
                          'Recommandé': { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
                          'Standard': { bg: '#e0e7ff', text: '#3730a3', border: '#6366f1' },
                          'Économique': { bg: '#f0f9ff', text: '#075985', border: '#0ea5e9' },
                          'Ultra-éco': { bg: '#f5f5f4', text: '#57534e', border: '#a8a29e' },
                        };
                        const tc = tierColors[m.tier] || tierColors['Standard'];
                        return (
                          <button type="button"
                            key={m.value}
                            onClick={() => setModelConfig({
                              ...modelConfig,
                              [selectedProvider]: { ...currentModelConfig, model: m.value },
                            })}
                            style={{
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: isSelected ? `2px solid ${providerInfo?.color}` : '1px solid var(--gray-200)',
                              background: isSelected ? `${providerInfo?.color}08` : '#fff',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              font: 'inherit',
                              color: 'inherit',
                              textAlign: 'left',
                              width: '100%',
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{m.label}</span>
                                <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '1px 6px', borderRadius: '4px', background: tc.bg, color: tc.text, border: `1px solid ${tc.border}30` }}>{m.tier}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.68rem', color: 'var(--gray-500)' }}>
                                <span title="Temps estimé">{m.speed}</span>
                                <span title="Max tokens sortie" style={{ fontFamily: 'Consolas, monospace', background: 'var(--gray-100)', padding: '0 4px', borderRadius: '3px' }}>{m.maxTok}</span>
                                <span title="Coût relatif" style={{ fontWeight: 600 }}>{m.cost}</span>
                              </div>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', lineHeight: 1.3 }}>{m.desc}</div>
                            <div className="d-flex align-items-center gap-1 mt-1">
                              {[1,2,3,4,5].map((star) => (
                                <span key={star} style={{ fontSize: '0.6rem', color: star <= m.quality ? providerInfo?.color : 'var(--gray-200)' }}>&#9733;</span>
                              ))}
                              <span style={{ fontSize: '0.6rem', color: 'var(--gray-400)', marginLeft: '4px' }}>Qualité</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Temperature slider */}
                  <div className="mb-4">
                    <label className="form-label d-flex align-items-center justify-content-between" htmlFor="field-provider-temperature" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <span className="d-flex align-items-center gap-2">
                        <FiThermometer style={{ color: ignoresTemperature ? 'var(--gray-400)' : providerInfo?.color }} />
                        Température
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: ignoresTemperature ? 'var(--gray-400)' : providerInfo?.color, fontFamily: 'Consolas, monospace', background: ignoresTemperature ? 'var(--gray-100)' : `${providerInfo?.color}15`, padding: '2px 10px', borderRadius: '6px' }}>
                        {ignoresTemperature ? 'N/A' : currentModelConfig.temperature.toFixed(1)}
                      </span>
                    </label>
                    <input
                      id="field-provider-temperature"
                      type="range"
                      className="form-range"
                      min="0" max="2" step="0.1"
                      value={currentModelConfig.temperature}
                      disabled={ignoresTemperature}
                      onChange={(e) => setModelConfig({ ...modelConfig, [selectedProvider]: { ...currentModelConfig, temperature: Number.parseFloat(e.target.value) } })}
                      style={{ accentColor: providerInfo?.color, opacity: ignoresTemperature ? 0.4 : 1 }}
                    />
                    <div className="d-flex justify-content-between" style={{ fontSize: '0.68rem', color: 'var(--gray-400)', opacity: ignoresTemperature ? 0.4 : 1 }}>
                      <span>Précis (0.0)</span><span>Créatif (1.0)</span>
                    </div>
                    <div className="form-text" style={{ fontSize: '0.72rem' }}>
                      {ignoresTemperature
                        ? `${selectedModelMeta?.label} est un modèle de raisonnement : l'API refuse toute température personnalisée et applique sa valeur par défaut (1). Choisissez un modèle GPT-4.1 pour régler ce paramètre.`
                        : 'Valeurs basses = résultats plus déterministes, valeurs hautes = plus de créativité'}
                    </div>
                  </div>

                  {/* Max tokens (sortie) */}
                  <div className="mb-4">
                    <label className="form-label d-flex align-items-center gap-2" htmlFor="field-provider-maxtokens" style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <FiSliders style={{ color: providerInfo?.color }} />
                      Max tokens (sortie)
                    </label>
                    <input
                      id="field-provider-maxtokens"
                      type="number"
                      min="256"
                      step="1024"
                      max={modelCeiling || undefined}
                      className="form-control"
                      placeholder={modelCeiling ? `Max du modèle : ${modelCeiling.toLocaleString()}` : 'Maximum du modèle'}
                      value={currentModelConfig.max_tokens ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        setModelConfig({
                          ...modelConfig,
                          [selectedProvider]: { ...currentModelConfig, max_tokens: v === '' ? null : Number.parseInt(v, 10) },
                        });
                      }}
                      style={{ fontSize: '0.82rem' }}
                    />
                    <div className="form-text" style={{ fontSize: '0.72rem' }}>
                      Nombre maximal de tokens générés en sortie. Augmentez-le pour les documents volumineux afin d'éviter la troncature des résultats. Laissez vide pour utiliser le maximum du modèle{modelCeiling ? ` (${modelCeiling.toLocaleString()} tokens)` : ''}.
                    </div>
                  </div>

                  {/* Save button */}
                  <button
                    className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleSaveModelConfig}
                    disabled={savingModel}
                    style={{ background: providerInfo?.color, borderColor: providerInfo?.color }}
                  >
                    <HiOutlineSave />
                    {savingModel ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
                  </button>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}
